import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useState } from "react";
import { CustomButton } from "src/Components/Common/Button/CustomButton";
import LoadingIcon from "src/Components/Common/LoadingIcon";
import { DIALOG_MAX_WIDTHS } from "src/Components/Common/Modal/ModalWidth";
import useFetch from "src/Components/Common/useFetch";
import { userContext } from "src/Components/Contexts/userContext";
import { UserRole } from "src/entity/models/FrontendActor";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { uiLoggerNamesInvoiceRejection } from "src/Utils/Recon/UiLogger/Constants";
import uiLogger from "src/Utils/UiLogger";
import { RejectedInvoicesPriority } from "./RejectedInvoices";

const BulkActionOfRejectedInvoices = ({
  openBulkActionOfRejectedInvoices,
  setOpenBulkActionOfRejectedInvoices,
  rowSelection,
  setRowSelection,
  allUsers,
  role,
  listSummaryData,
  listOpenInvoices,
  listCloseInvoices,
  filters,
}) => {
  const { actor } = useContext(userContext);
  const [allowUpdatingMaker, setAllowUpdatingMaker] = useState(false);
  const [allowUpdatingChecker, setAllowUpdatingChecker] = useState(false);
  const [allowUpdatingApprover, setAllowUpdatingApprover] = useState(false);

  const [allowUpdatingPriority, setAllowUpdatingPriority] = useState(false);

  const [allowUpdatingComments, setAllowUpdatingComments] = useState(false);
  const [markAllCommentsRead, setMarkAllCommentsRead] = useState(false);
  const [updateAllInViniculum, setUpdateAllInViniculum] = useState(false);

  const [currentMaker, setCurrentMaker] = useState([]);
  const [currentChecker, setCurrentChecker] = useState([]);
  const [currentApprover, setCurrentApprover] = useState([]);
  const [currentPriority, setCurrentPriority] = useState(null);
  const [currentComments, setCurrentComments] = useState(null);
  const [isLoadingBulkUpdate, setIsLoadingBulkUpdate] = useState(false);

  const handleClose = () => {
    // Logic to close the dialog
    setOpenBulkActionOfRejectedInvoices(false);
    setAllowUpdatingMaker(false);
    setAllowUpdatingChecker(false);
    setAllowUpdatingApprover(false);
    setAllowUpdatingPriority(false);
    setAllowUpdatingComments(false);
    setCurrentMaker([]);
    setCurrentChecker([]);
    setCurrentApprover([]);
    setCurrentPriority(null);
    setCurrentComments(null);
    setMarkAllCommentsRead(false);
    setUpdateAllInViniculum(false);
  };

  const handleBulkUpdate = () => {
    setIsLoadingBulkUpdate(true);

    useFetch(API_ENDPOINTS.BULK_UPDATE_INVOICES(role).url, "POST", {
      failureMessage: API_ENDPOINTS.BULK_UPDATE_INVOICES(role).failureMessage,

      data: {
        makers: allowUpdatingMaker ? currentMaker.map((maker) => maker.id) : null, // Extracts only the IDs from currentMaker
        checkers: allowUpdatingChecker ? currentChecker.map((checker) => checker.id) : null, // Extracts only the IDs from currentChecker
        approvers: allowUpdatingApprover ? currentApprover.map((approver) => approver.id) : null, // Extracts only the IDs from currentApprover
        priority: allowUpdatingPriority ? currentPriority : null,
        // Always the explicit selection. The list is server-paginated, so the rows in the browser
        // are one page — an empty grnIds means "every invoice matching the filters" to the API,
        // which selecting a full page must never trigger.
        grnIds: Object.entries(rowSelection)
          .filter(([_, isSelected]) => isSelected)
          .map(([grnNumber, _]) => grnNumber),
        comment: allowUpdatingComments ? currentComments : null,
        markAllCommentAsRead: markAllCommentsRead,
        markUpdateInVini: updateAllInViniculum,
        filters: filters,
      },
      showSuccessToast: true,
      thenCallBack: () => {
        setRowSelection({});
        listSummaryData();
        listOpenInvoices();
        listCloseInvoices();

        setIsLoadingBulkUpdate(false);
        handleClose();
      },
      catchCallBack: () => {
        setIsLoadingBulkUpdate(false);
      },
    });

    uiLogger(uiLoggerNamesInvoiceRejection.UI_SST_IR_BULKACTIONS_UPDATE_CLICK.functionName, null, null, {
      message: uiLoggerNamesInvoiceRejection.UI_SST_IR_BULKACTIONS_UPDATE_CLICK.message,
    });
  };
  return (
    <Dialog open={openBulkActionOfRejectedInvoices} maxWidth={DIALOG_MAX_WIDTHS.SM} fullWidth>
      <DialogTitle className="dialog-title">
        <div className="heading-m">Bulk Actions</div>
        <div className="vertical_center_align">
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        <Stack sx={{ gap: 2 }}>
          <Typography variant="subtitle2" sx={{ color: "rgba(0, 0, 0, 0.6)", marginBottom: 1 }}>
            {Object.values(rowSelection)?.filter(Boolean)?.length} Tasks selected
          </Typography>
          {(actor.userRole === UserRole.DRApprover ||
            actor.userRole === UserRole.DRChecker ||
            actor.userRole === UserRole.ReconDRApprover) && (
            <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
              <Checkbox checked={allowUpdatingMaker} onChange={(e) => setAllowUpdatingMaker(e.target.checked)} />
              <Autocomplete
                multiple
                size="small"
                // value={currentMaker}
                disabled={!allowUpdatingMaker}
                onChange={(_, newValue) => {
                  setCurrentMaker(newValue);
                }}
                sx={{ width: "100%", "& fieldset": { borderRadius: "4px" } }}
                disableClearable
                options={allUsers} // Show all users
                renderOption={(props, item) => (
                  <li {...props} key={item.id}>
                    <ListItemText>{item.name}</ListItemText>
                  </li>
                )}
                getOptionLabel={(option: { name: string; id: number }) => option.name}
                renderInput={(params) => <TextField {...params} label="Maker" size="small" />}
              />
            </Box>
          )}
          {(actor.userRole === UserRole.DRApprover ||
            actor.userRole === UserRole.DRChecker ||
            actor.userRole === UserRole.ReconDRApprover) && (
            <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
              <Checkbox checked={allowUpdatingChecker} onChange={(e) => setAllowUpdatingChecker(e.target.checked)} />
              <Autocomplete
                multiple
                size="small"
                // value={currentChecker}
                disabled={!allowUpdatingChecker}
                onChange={(_, newValue) => {
                  setCurrentChecker(newValue);
                }}
                sx={{ width: "100%", "& fieldset": { borderRadius: "4px" } }}
                disableClearable
                options={allUsers?.filter(
                  (user) =>
                    user.role === UserRole.DRChecker ||
                    user.role === UserRole.DRApprover ||
                    user.role === UserRole.ReconDRApprover
                )} // Filter users by role
                renderOption={(props, item) => (
                  <li {...props} key={item.id}>
                    <ListItemText>{item.name}</ListItemText>
                  </li>
                )}
                getOptionLabel={(option: { name: string; id: number }) => option.name}
                renderInput={(params) => <TextField {...params} label="Checker" size="small" />}
              />
            </Box>
          )}

          {(actor.userRole === UserRole.DRApprover || actor.userRole === UserRole.ReconDRApprover) && (
            <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
              <Checkbox checked={allowUpdatingApprover} onChange={(e) => setAllowUpdatingApprover(e.target.checked)} />
              <Autocomplete
                multiple
                size="small"
                // value={currentApprover}
                disabled={!allowUpdatingApprover}
                onChange={(_, newValue) => {
                  setCurrentApprover(newValue);
                }}
                sx={{ width: "100%", "& fieldset": { borderRadius: "4px" } }}
                disableClearable
                options={allUsers?.filter(
                  (user) => user.role === UserRole.DRApprover || actor.userRole === UserRole.ReconDRApprover
                )} // Filter users by role
                renderOption={(props, item) => (
                  <li {...props} key={item.id}>
                    <ListItemText>{item.name}</ListItemText>
                  </li>
                )}
                getOptionLabel={(option: { name: string; id: number }) => option.name}
                renderInput={(params) => <TextField {...params} label="Approver" size="small" />}
              />
            </Box>
          )}
          {(actor.userRole === UserRole.DRApprover ||
            actor.userRole === UserRole.DRChecker ||
            actor.userRole === UserRole.ReconDRApprover) && (
            <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
              <Checkbox checked={allowUpdatingPriority} onChange={(e) => setAllowUpdatingPriority(e.target.checked)} />
              <Autocomplete
                // value={currentPriority}
                disabled={!allowUpdatingPriority}
                onChange={(_, newValue) => {
                  setCurrentPriority(newValue);
                }}
                sx={{ width: "100%", "& fieldset": { borderRadius: "4px" } }}
                disableClearable
                options={Object.values(RejectedInvoicesPriority)}
                renderOption={(props, item) => (
                  <li {...props} key={item}>
                    <ListItemText>{item}</ListItemText>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Priority" size="small" />}
              />
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
            <Checkbox checked={allowUpdatingComments} onChange={(e) => setAllowUpdatingComments(e.target.checked)} />
            <TextField
              // value={currentComments}
              disabled={!allowUpdatingComments}
              InputProps={{
                style: {
                  borderRadius: "4px",
                },
              }}
              sx={{ width: "100%" }}
              size="small"
              label="Add a comment..."
              onChange={(e) => {
                const remarks = e.target?.value.trim();
                setCurrentComments(remarks.length > 0 ? remarks : null);
              }}
              multiline
              maxRows={4}
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center" }}>
            <Checkbox checked={markAllCommentsRead} onChange={(e) => setMarkAllCommentsRead(e.target.checked)} />
            <Typography className="heading-xs">Mark all comments as read</Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center" }}>
            <Checkbox checked={updateAllInViniculum} onChange={(e) => setUpdateAllInViniculum(e.target.checked)} />
            <Typography className="heading-xs">Mark as "Attachments updated in Viniculum"</Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions className="dialog-actions">
        <CustomButton size="medium" colorToken="violet" variant="text" onClick={handleClose}>
          Cancel
        </CustomButton>
        <CustomButton
          size="medium"
          colorToken="violet"
          variant="contained"
          disabled={isLoadingBulkUpdate}
          startIcon={<LoadingIcon loading={isLoadingBulkUpdate} />}
          onClick={() => {
            handleBulkUpdate();
          }}
        >
          Update
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default BulkActionOfRejectedInvoices;
